import { useState, useCallback } from 'react';

interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface PlaceDetails {
  formatted_address: string;
  name: string;
  place_id: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

interface PlacesAutocompleteResponse {
  predictions: PlacePrediction[];
  status: string;
}

interface PlacesDetailsResponse {
  statusCode: number;
  message: string;
  data: PlaceDetails;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8084';

export const useGoogleMaps = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchPlaces = useCallback(
    async (
      input: string,
      options?: {
        language?: string;
        lat?: number;
        lng?: number;
        radius?: number;
        types?: string;
      }
    ): Promise<PlacePrediction[]> => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          input,
          ...(options?.language && { language: options.language }),
          ...(options?.lat && { lat: options.lat.toString() }),
          ...(options?.lng && { lng: options.lng.toString() }),
          ...(options?.radius && { radius: options.radius.toString() }),
          ...(options?.types && { types: options.types }),
        });

        const response = await fetch(
          `${API_BASE_URL}/google-maps/places/autocomplete?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: PlacesAutocompleteResponse = await response.json();

        if (data.status !== 'OK') {
          throw new Error(`Google Places API error: ${data.status}`);
        }

        return data.predictions;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error searching places:', errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getPlaceDetails = useCallback(
    async (
      placeId: string,
      options?: {
        language?: string;
        fields?: string;
      }
    ): Promise<PlaceDetails | null> => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          place_id: placeId,
          ...(options?.language && { language: options.language }),
          ...(options?.fields && { fields: options.fields }),
        });

        const response = await fetch(
          `${API_BASE_URL}/google-maps/places/details?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: PlacesDetailsResponse = await response.json();

        // 백엔드 응답 구조에 맞게 수정
        // Google Places API 응답에는 status 필드가 없고, data 안에 바로 결과가 들어있음
        if (!data.data) {
          throw new Error('Google Places Details API error: No data received');
        }

        return data.data as PlaceDetails;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error getting place details:', errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const geocodeAddress = useCallback(
    async (address: string, language: string = 'ko') => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          address,
          language,
        });

        const response = await fetch(
          `${API_BASE_URL}/google-maps/geocode?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status !== 'OK') {
          throw new Error(`Google Geocoding API error: ${data.status}`);
        }

        return data.results;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error geocoding address:', errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reverseGeocode = useCallback(
    async (lat: number, lng: number, language: string = 'ko') => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          lat: lat.toString(),
          lng: lng.toString(),
          language,
        });

        const response = await fetch(
          `${API_BASE_URL}/google-maps/reverse-geocode?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status !== 'OK') {
          throw new Error(`Google Reverse Geocoding API error: ${data.status}`);
        }

        return data.results;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error reverse geocoding:', errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    searchPlaces,
    getPlaceDetails,
    geocodeAddress,
    reverseGeocode,
    loading,
    error,
  };
};
